/**
 * What the mail rail can open, and how a Favorites pin names it.
 *
 * The rail draws one folder tree per mailbox (the shared info@, then the
 * reader's own kristi@), each with the same six rows, plus Favorites on top.
 * Every one of those rows opens a `MailView`; a pin is the same view written
 * as a short string, so a favourite and the row it copies can never open two
 * different things. Pure - no React, no requests - so it is tested on its own.
 *
 * Rows by what is behind them:
 * - Inbox, Archive, Spam: the CRM's own conversations, filtered by status.
 * - Drafts, Scheduled: the reader's unsent letters in that mailbox.
 * - Sent: the mailbox's Sent folder on the mail server, read live. The CRM
 *   files a copy of every letter it sends there, so it is complete.
 * - a server folder ("More folders"): read live, like Sent.
 *
 * "all" is every mailbox together. Only Inbox and Sent come in that form.
 */
import type { ImapFolder, ThreadStatus } from "../../../../lib/mail";

export const SHARED = "shared";
export const ALL = "all";

export type BoxRow = "inbox" | "drafts" | "scheduled" | "sent" | "archived" | "spam";

/** The order each mailbox draws its rows in. */
export const BOX_ROWS: BoxRow[] = ["inbox", "drafts", "scheduled", "sent", "archived", "spam"];

export type MailView =
  | { kind: "threads"; mailbox: string; status: ThreadStatus }
  | { kind: "drafts"; mailbox: string; scheduled: boolean }
  | { kind: "sent"; mailbox: string }
  | { kind: "sentAll" }
  | { kind: "folder"; mailbox: string; path: string };

/** Where the page opens: the shared mailbox's inbox, as it always has. */
export const START_VIEW: MailView = { kind: "threads", mailbox: SHARED, status: "open" };

const STATUS: Partial<Record<BoxRow, ThreadStatus>> = { inbox: "open", archived: "archived", spam: "spam" };

/** One mailbox's row as a view. */
export function rowView(mailbox: string, row: BoxRow): MailView {
  const status = STATUS[row];
  if (status) return { kind: "threads", mailbox, status };
  if (row === "drafts" || row === "scheduled") return { kind: "drafts", mailbox, scheduled: row === "scheduled" };
  return mailbox === ALL ? { kind: "sentAll" } : { kind: "sent", mailbox };
}

/** The view as a pin (the strings `crm-email/lib/favorites.ts` stores). */
export function viewRef(view: MailView): string {
  switch (view.kind) {
    case "threads":
      return `${view.mailbox}:${view.status === "open" ? "inbox" : view.status}`;
    case "drafts":
      return `${view.mailbox}:${view.scheduled ? "scheduled" : "drafts"}`;
    case "sent":
      return `${view.mailbox}:sent`;
    case "sentAll":
      return `${ALL}:sent`;
    case "folder":
      return `${view.mailbox}:folder:${view.path}`;
  }
}

/** A pin back into a view, or null for one this rail does not know. */
export function refView(ref: string): MailView | null {
  const colon = ref.indexOf(":");
  if (colon <= 0) return null;
  const mailbox = ref.slice(0, colon);
  const rest = ref.slice(colon + 1);
  if (rest.startsWith("folder:")) {
    const path = rest.slice("folder:".length);
    return mailbox !== ALL && path ? { kind: "folder", mailbox, path } : null;
  }
  if (!(BOX_ROWS as string[]).includes(rest)) return null;
  if (mailbox === ALL && rest !== "inbox" && rest !== "sent") return null;
  return rowView(mailbox, rest as BoxRow);
}

export function sameView(a: MailView, b: MailView): boolean {
  return viewRef(a) === viewRef(b);
}

/** The one mailbox a view is about, or null for a combined one. */
export function viewMailbox(view: MailView): string | null {
  if (view.kind === "sentAll") return null;
  return view.mailbox === ALL ? null : view.mailbox;
}

/**
 * Whether a pin can still be drawn: its mailbox must still be one the reader
 * has. A removed mailbox leaves its pins behind in the stored list; they are
 * not drawn, and the next save drops them.
 */
export function pinUsable(ref: string, mailboxIds: ReadonlySet<string>): boolean {
  const view = refView(ref);
  if (!view) return false;
  const box = viewMailbox(view);
  return box === null || mailboxIds.has(box);
}

/**
 * The mailbox ids after `id` is dropped before or after `target`: what a drag of one
 * mailbox heading onto another does. Unchanged when either is not in the list.
 */
export function moveMailbox(ids: string[], id: string, target: string, after: boolean): string[] {
  if (id === target || !ids.includes(id) || !ids.includes(target)) return ids;
  const rest = ids.filter((x) => x !== id);
  const at = rest.indexOf(target) + (after ? 1 : 0);
  return [...rest.slice(0, at), id, ...rest.slice(at)];
}

/** Pin or unpin. A new pin goes to the end, the way it was asked for last. */
export function togglePin(list: string[], ref: string): string[] {
  return list.includes(ref) ? list.filter((r) => r !== ref) : [...list, ref];
}

/** The server folders a mailbox's tree already shows as its own rows. */
export function isMainFolder(f: Pick<ImapFolder, "path" | "specialUse">): boolean {
  return f.path.toUpperCase() === "INBOX" || f.specialUse === "\\Sent";
}

/**
 * A mailbox's Sent folder: the one the server flags, else one named like it.
 * The same rule the server files its copies by (`sentCopy.ts`).
 */
export function sentPath(folders: Pick<ImapFolder, "path" | "name" | "specialUse">[]): string | null {
  const flagged = folders.find((f) => f.specialUse === "\\Sent");
  if (flagged) return flagged.path;
  const names = new Set(["sent", "sent items", "sent messages", "sent mail", "saadetud", "saadetud kirjad"]);
  return folders.find((f) => names.has(f.name.trim().toLowerCase()))?.path ?? null;
}

/** The thread list's query for a threads view. */
export function threadQueryOf(view: Extract<MailView, { kind: "threads" }>): { status: ThreadStatus; mailbox: string } {
  return { status: view.status, mailbox: view.mailbox };
}
